import { useEffect, useState } from 'react';
import type { QuoteResponse, WorkingOrderResponse } from '@biasafe.models';

/**
 * How fresh the Execution screens are, and how honestly they say so.
 *
 * Pure helpers (poll intervals, status wording, time formatting) live here so
 * the rules are tested in one place and every tab applies the same ones.
 */

export const QUOTE_POLL_MS = 5_000;
export const ORDERS_FAST_POLL_MS = 5_000;
export const ORDERS_SLOW_POLL_MS = 30_000;
export const TRADES_POLL_MS = 15_000;

/** True while the browser tab is on screen. */
export function isDocumentVisible(): boolean {
  return (
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
}

/** Tracks tab visibility so a poll can pause while the tab is hidden. */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(isDocumentVisible);
  useEffect(() => {
    const onChange = () => setVisible(isDocumentVisible());
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/** A fixed poll that stops while the tab is hidden. */
export function visiblePollInterval(
  ms: number,
  visible: boolean,
): number | false {
  return visible ? ms : false;
}

// ── Orders ───────────────────────────────────────────────────────────────

export type OrderStatusLabel =
  'Working' | 'Partly filled' | 'Filled' | 'Cancelled' | 'Rejected' | 'Pending';

export interface FriendlyOrderStatus {
  label: OrderStatusLabel;
  /** Nothing more will happen to this order. */
  terminal: boolean;
}

type OrderStatusInput = Pick<
  WorkingOrderResponse,
  'status' | 'totalSize' | 'remaining' | 'filledQuantity'
>;

/** Filled quantity: IBKR's own figure, else what is missing from the total. */
export function orderFilledQuantity(o: OrderStatusInput): number {
  if (o.filledQuantity != null && o.filledQuantity > 0) return o.filledQuantity;
  const derived = o.totalSize - o.remaining;
  return o.remaining > 0 && derived > 0 ? derived : 0;
}

/**
 * IBKR's status string plus filled and remaining quantity, in words a person
 * uses. The raw string stays available for a tooltip: IBKR's states
 * ("PreSubmitted", "Inactive") are not ours to rename away.
 */
export function friendlyOrderStatus(o: OrderStatusInput): FriendlyOrderStatus {
  const raw = o.status.trim().toLowerCase();
  if (raw === 'filled') return { label: 'Filled', terminal: true };
  if (raw === 'cancelled' || raw === 'apicancelled') {
    return { label: 'Cancelled', terminal: true };
  }
  if (raw === 'inactive' || raw === 'rejected') {
    return { label: 'Rejected', terminal: true };
  }
  if (
    raw === 'pendingsubmit' ||
    raw === 'pendingcancel' ||
    raw === 'presubmitted'
  ) {
    return { label: 'Pending', terminal: false };
  }
  const filled = orderFilledQuantity(o);
  if (o.totalSize > 0 && o.remaining <= 0 && filled > 0) {
    return { label: 'Filled', terminal: true };
  }
  if (filled > 0) return { label: 'Partly filled', terminal: false };
  // Submitted, and anything IBKR adds later that we do not recognise: it is
  // still on the blotter, so it is treated as live.
  return { label: 'Working', terminal: false };
}

/** Fast while something can still change, slow otherwise, off while hidden. */
export function ordersPollInterval(
  orders: ReadonlyArray<OrderStatusInput> | undefined,
  visible: boolean,
): number | false {
  if (!visible) return false;
  const anyOpen = (orders ?? []).some((o) => !friendlyOrderStatus(o).terminal);
  return anyOpen ? ORDERS_FAST_POLL_MS : ORDERS_SLOW_POLL_MS;
}

// ── Quotes ───────────────────────────────────────────────────────────────

const QUOTE_BADGES: Record<string, string | undefined> = {
  delayed: 'Delayed',
  prior_close: 'Prior close',
  halted: 'Halted',
};

/** The badge text for a quote that is not plainly live, else null. */
export function quoteBadgeLabel(
  state: QuoteResponse['quoteState'],
): string | null {
  return (state && QUOTE_BADGES[state]) || null;
}

/** "as of 14:03:05" in the viewer's zone; null when there is no usable time. */
export function formatQuoteAsOf(
  asOf: string | null | undefined,
): string | null {
  if (!asOf) return null;
  const d = new Date(asOf);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** A bid or ask: "—" when IBKR sent none, never "$0.00". */
export function fmtBidAsk(
  n: number | null | undefined,
  fmt: (n: number) => string,
): string {
  return n == null || n <= 0 ? '—' : fmt(n);
}

// ── Trades ───────────────────────────────────────────────────────────────

/**
 * A fill's date, time and time zone, e.g. "Oct 2, 2026, 14:03:05 CEST".
 *
 * Uses the epoch-ms stamp when the API has one. Otherwise IBKR's
 * `YYYYMMDD-HH:MM:SS`, which IBKR documents as UTC, is read as UTC. A string
 * in neither shape is passed through untouched rather than guessed at.
 */
export function formatFillTime(
  tradeTimeMs: number | null | undefined,
  tradeTime: string | null | undefined,
): string {
  let ms: number | null = null;
  if (typeof tradeTimeMs === 'number' && Number.isFinite(tradeTimeMs)) {
    ms = tradeTimeMs;
  } else {
    const m = (tradeTime ?? '').match(
      /^(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2}):?(\d{2})?/,
    );
    if (m)
      ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], Number(m[6] || 0));
  }
  if (ms == null) return tradeTime ?? '';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}
